import React, { FunctionComponent, useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { styles } from '../../../styles/themes/style'
import { isChromeBasedBrowser, isMobileDevice } from '../../../utils'
import { useAuthHandlers } from '../../../services/actions/auth/useAuthHandlers'
import { useAuthStore } from '../../../stores/auth/useAuthStore'
import { useAppActions } from '../../../stores/application/useAppActions'
import { useAppStore } from '../../../stores/application/useAppStore'
import { AppRoutes } from '../../../interfaces/store/appStore'
import OnboardingStepLayout from '../components/OnboardingStepLayout'
import {
    ONBOARDING_SKIP_STEPPER_STEPS_BROWSER,
    ONBOARDING_SKIP_STEPPER_STEPS_EXTENSION,
} from '../components/OnboardingStepper'

const SURVEY_QUESTION = 'Where did you first hear about LeadDelta?'

export const HEAR_ABOUT_LEADDELTA_OPTIONS = [
    'Google search',
    'AI: Gemini, Claude, Perplexity, ChatGPT',
    'LinkedIn',
    'Chrome Web Store',
    'Advertising',
    'YouTube or X',
    'Podcast, newsletter, blog',
    'Reddit, community, forum',
    'Event, webinar, workshop',
    'From a friend or colleague',
    'Other',
] as const

export type HearAboutLeadDeltaOption = (typeof HEAR_ABOUT_LEADDELTA_OPTIONS)[number]

const HEAR_ABOUT_OPTIONS_WITH_DETAIL: HearAboutLeadDeltaOption[] = [
    'From a friend or colleague',
    'Other',
]

export const hearAboutOptionRequiresDetail = (
    option: HearAboutLeadDeltaOption | '',
): option is HearAboutLeadDeltaOption =>
    Boolean(option && HEAR_ABOUT_OPTIONS_WITH_DETAIL.includes(option as HearAboutLeadDeltaOption))

const DETAIL_PLACEHOLDERS: Partial<Record<HearAboutLeadDeltaOption, string>> = {
    'From a friend or colleague': 'Who referred you? (name, company, etc.)',
    Other: 'Please tell us where you heard about us',
}

export const formatHearAboutLeadDeltaValue = (
    option: HearAboutLeadDeltaOption,
    detail: string,
): string => {
    const trimmed = detail.trim()
    if (hearAboutOptionRequiresDetail(option) && trimmed) {
        return `${option}: ${trimmed}`
    }
    return option
}

const SurveySection: FunctionComponent = () => {
    const [selected, setSelected] = useState<HearAboutLeadDeltaOption | ''>('')
    const [detail, setDetail] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const { saveUserPreferencesHandler } = useAuthHandlers()
    const { store: { user } } = useAuthStore()
    const { store: { extensionInstalled } } = useAppStore()
    const { setOnboardingStep } = useAppActions()
    const navigate = useNavigate()

    const skipFlow = !extensionInstalled
    const surveyStepperSteps = skipFlow
        ? (isChromeBasedBrowser()
            ? ONBOARDING_SKIP_STEPPER_STEPS_EXTENSION
            : ONBOARDING_SKIP_STEPPER_STEPS_BROWSER)
        : undefined
    const surveyStepIndex = skipFlow ? 1 : 3

    const canContinue = Boolean(selected)

    const handleContinue = async () => {
        if (!selected || !canContinue || submitting) return
        setSubmitting(true)
        try {
            await saveUserPreferencesHandler({
                ...(user?.preferences ?? {}),
                hearAboutLeadDelta: formatHearAboutLeadDeltaValue(selected, detail),
            })
            setOnboardingStep(5)
            navigate(
                isMobileDevice() ? AppRoutes.ONBOARDING_MOBILE_FINISH : AppRoutes.ONBOARDING_ALL_SET,
            )
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <OnboardingStepLayout
            activeStepIndex={surveyStepIndex}
            steps={surveyStepperSteps}
            title="One quick question. It helps us improve."
        >
            <SurveyCard>
                <SurveyCardInner>
                    <QuestionText>{SURVEY_QUESTION}</QuestionText>
                    <OptionsList role="radiogroup" aria-label={SURVEY_QUESTION}>
                        {HEAR_ABOUT_LEADDELTA_OPTIONS.map(option => {
                            const checked = selected === option
                            const showDetail = checked && hearAboutOptionRequiresDetail(option)
                            return (
                                <OptionBlock key={option}>
                                    <OptionRow>
                                        <RadioInput
                                            type="radio"
                                            name="hearAboutLeadDelta"
                                            value={option}
                                            checked={checked}
                                            onChange={() => {
                                                setSelected(option)
                                                if (!hearAboutOptionRequiresDetail(option)) {
                                                    setDetail('')
                                                }
                                            }}
                                        />
                                        <OptionLabel>{option}</OptionLabel>
                                    </OptionRow>
                                    {showDetail && (
                                        <DetailInput
                                            type="text"
                                            value={detail}
                                            placeholder={DETAIL_PLACEHOLDERS[option]}
                                            maxLength={200}
                                            aria-label={
                                                option === 'Other'
                                                    ? 'Other source details'
                                                    : 'Referral details'
                                            }
                                            onChange={e => setDetail(e.target.value)}
                                        />
                                    )}
                                </OptionBlock>
                            )
                        })}
                    </OptionsList>
                </SurveyCardInner>

                <ContinueButton
                    type="button"
                    $active={canContinue}
                    disabled={!canContinue || submitting}
                    onClick={() => void handleContinue()}
                >
                    {submitting ? 'Saving…' : 'Continue'}
                </ContinueButton>
            </SurveyCard>
        </OnboardingStepLayout>
    )
}

export default SurveySection

const SurveyCard = styled.div`
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    padding: 32px 24px 24px;
    gap: 32px;
    width: 100%;
    border: 1px solid ${styles.colors.black200};
    border-radius: 12px;
    background: ${styles.colors.white};
`

const SurveyCardInner = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
`

const QuestionText = styled.p`
    margin: 0;
    font-family: NunitoSans;
    font-weight: 400;
    font-size: 18px;
    line-height: 24px;
    color: ${styles.colors.black400};
`

const OptionsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
`

const OptionBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
`

const OptionRow = styled.label`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
    cursor: pointer;
    width: 100%;
`

const RadioInput = styled.input`
    width: 16px;
    height: 16px;
    margin: 0;
    flex-shrink: 0;
    accent-color: ${styles.colors.primary600};
    cursor: pointer;
`

const OptionLabel = styled.span`
    font-family: NunitoSans;
    font-weight: 400;
    font-size: 16px;
    line-height: 20px;
    color: ${styles.colors.black600};
`

const DetailInput = styled.input`
    box-sizing: border-box;
    width: 100%;
    margin-left: 28px;
    max-width: calc(100% - 28px);
    height: 43px;
    padding: 0 12px;
    border-radius: 12px;
    border: 1px solid ${styles.colors.black200};
    background: ${styles.colors.white};
    font-family: NunitoSans;
    font-size: 14px;
    line-height: 20px;
    color: ${styles.colors.black600};

    &::placeholder {
        color: ${styles.colors.black300};
    }

    &:focus {
        outline: none;
        border-color: ${styles.colors.primary600};
    }
`

const ContinueButton = styled.button<{ $active: boolean }>`
    box-sizing: border-box;
    width: 100%;
    min-height: 52px;
    padding: 0 24px;
    border-radius: 12px;
    border: 1px solid
        ${({ $active }) => ($active ? styles.colors.primary600 : styles.colors.primary200)};
    background-color: ${({ $active }) =>
        $active ? styles.colors.primary600 : styles.colors.primary200};
    color: ${styles.colors.white};
    font-family: NunitoSans;
    font-weight: 700;
    font-size: 14px;
    line-height: 20px;
    cursor: ${({ $active }) => ($active ? 'pointer' : 'not-allowed')};
    opacity: 1;
    transition: background-color 0.2s ease, border-color 0.2s ease;

    &:hover:not(:disabled) {
        background-color: ${styles.colors.primary400};
        border-color: ${styles.colors.primary400};
    }

    &:focus {
        outline: none;
    }

    &:disabled {
        opacity: 1;
    }
`
